import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Admin AI Cost & Usage aggregate.
 *
 * Backed by `GET /api/admin/ai/usage?days=N` (see
 * `apps/api/src/routes/admin-ai-usage.routes.ts`). Cost data is not
 * real-time critical, so a 60 s `staleTime` matches the server-side cache TTL.
 */

export type AIUsageWindow = 7 | 30 | 90

export interface AIUsageTotals {
  cost: number
  inputTokens: number
  /** Answer tokens only. The billed output total is this + `reasoningTokens`. */
  outputTokens: number
  /** KAN-818: separately-billed reasoning tokens in the window. */
  reasoningTokens: number
  /**
   * KAN-818: rows whose reasoning was never attributed. Non-zero means `cost`
   * and `totalTokens` are lower bounds, not measurements.
   */
  reasoningUnknownRequests: number
  /** Input + answer + reasoning — every billed token in the window. */
  totalTokens: number
  requests: number
  /** Fraction 0..1. */
  successRate: number
  /** Cost / distinct articles in window; 0 if no articles. */
  costPerArticle: number
  /** % change vs prior same-length window; null when no prior data. */
  momDelta: number | null
  /** Count of `rate_limited=true` rows in window. */
  rateLimitedCount: number
}

export interface AIUsageDailyPoint {
  /** UTC day, formatted YYYY-MM-DD. */
  day: string
  cost: number
  requests: number
}

export interface AIUsageBreakdownEntry {
  task: string
  providerId: string
  providerName: string
  model: string
  cost: number
  requests: number
  inputTokens: number
  outputTokens: number
  /** Count of `rate_limited=true` rows for this (task, provider, model). */
  rateLimitedCount: number
}

export interface AIUsageResponse {
  window: { days: number }
  totals: AIUsageTotals
  daily: AIUsageDailyPoint[]
  breakdown: AIUsageBreakdownEntry[]
  /** ISO timestamp — when the server produced this aggregate. */
  generatedAt: string
}

export const aiUsageQueryKeys = {
  all: ['ai-usage'] as const,
  window: (days: number) => ['ai-usage', days] as const,
}

/** Load the AI cost & usage aggregate for a rolling window (default 7 days). */
export function useAIUsage(opts: { days?: AIUsageWindow } = {}) {
  const days = opts.days ?? 7
  return useQuery({
    queryKey: aiUsageQueryKeys.window(days),
    queryFn: () => api.get<AIUsageResponse>(`/admin/ai/usage?days=${days}`),
    // Cost data is not real-time critical; 60 s matches the server-side cache.
    staleTime: 60_000,
  })
}
